import { Dialog, DialogContent, MenuItem, Tooltip } from '@material-ui/core';
import { ChangeEvent, FunctionComponent, useCallback, useState } from 'react';

import { USER_STATUS_LABELS, UserStatus } from '../../../../enums/user';
import { useAppDispatch } from '../../../../hooks';
import { updateCommunityMemberStatus } from '../../../../reducers/dashboard';
import {
    ArchiveButton,
    CancelButton,
    dialogPaperProps,
    ModalActions,
    ModalContentText,
    ModalTitle,
    selectMenuProps,
    StyledSelect,
    TooltipTarget
} from './styled';

type Props = {
    userId: string;
    status: UserStatus;
    lastStatusUpdated?: string;
};

const formatLastStatusUpdated = (raw?: string): string => {
    if (!raw) {
        return 'Last updated: unknown';
    }
    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) {
        return `Last updated: ${raw}`;
    }
    return `Last updated: ${parsed.toLocaleString()}`;
};

const AvailabilityCell: FunctionComponent<Props> = ({
    userId,
    status,
    lastStatusUpdated
}: Props) => {
    const dispatch = useAppDispatch();
    const [ pendingArchive, setPendingArchive ] = useState<boolean>(false);
    const [ submitting, setSubmitting ] = useState<boolean>(false);

    const dispatchUpdate = useCallback((next: UserStatus) => {
        setSubmitting(true);
        dispatch(updateCommunityMemberStatus({ userId, status: next }))
            .finally(() => setSubmitting(false));
    }, [ dispatch, userId ]);

    const handleChange = useCallback((event: ChangeEvent<{ value: unknown }>) => {
        const next = event.target.value as UserStatus;
        if (next === status) {
            return;
        }
        if (next === UserStatus.ARCHIVE) {
            setPendingArchive(true);
            return;
        }
        dispatchUpdate(next);
    }, [ dispatchUpdate, status ]);

    const handleConfirmArchive = useCallback(() => {
        setPendingArchive(false);
        dispatchUpdate(UserStatus.ARCHIVE);
    }, [ dispatchUpdate ]);

    const handleCancelArchive = useCallback(() => {
        setPendingArchive(false);
    }, []);

    return (
        <>
            <Tooltip title={formatLastStatusUpdated(lastStatusUpdated)} placement="top" arrow>
                <TooltipTarget>
                    <StyledSelect
                        value={status}
                        onChange={handleChange}
                        disabled={submitting}
                        variant="outlined"
                        MenuProps={selectMenuProps}
                    >
                        {Object.values(UserStatus).map((option) => (
                            <MenuItem key={option} value={option}>
                                {USER_STATUS_LABELS[option]}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                </TooltipTarget>
            </Tooltip>
            <Dialog
                open={pendingArchive}
                onClose={handleCancelArchive}
                PaperProps={dialogPaperProps}
            >
                <DialogContent>
                    <ModalTitle variant="h6">Archive contributor?</ModalTitle>
                    <ModalContentText>
                        Archiving will remove all of this user&apos;s contributor tags
                        and they will be disconnected within an hour. They will not be
                        able to sign back in. Continue?
                    </ModalContentText>
                    <ModalActions>
                        <CancelButton onClick={handleCancelArchive}>
                            Cancel
                        </CancelButton>
                        <ArchiveButton onClick={handleConfirmArchive}>
                            Archive
                        </ArchiveButton>
                    </ModalActions>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AvailabilityCell;
